import { notifyMfaEnrollmentRequiredFromResponse } from './mfa-enrollment-client';
import { notifyUnauthenticated } from './use-session-broadcast';

/** One destination in the switcher; the server sends only what the organization owns. */
export type ProductDirectoryEntry = {
  name: string;
  label: string;
  badge: string;
  description: string;
  category: string;
  url: string | null;
  plan: string;
  weight: number;
};

export type ProductDirectoryCategory = {
  key: string;
  label: string;
};

/** One job the open product exists to get done, and where inside it that happens. */
export type ProductDirectoryJob = {
  token: string;
  label: string;
  path: string;
};

/** One application the open product opens onto, drawn as its own tile. */
export type ProductDirectoryApplication = {
  token: string;
  label: string;
  path: string;
  /** A Material Symbols name, drawn in place of a product monogram. */
  icon: string;
};

/** A favourited product, action or application, already resolved for drawing, in the arranged order. */
export type ProductDirectoryShortcut = {
  token: string;
  label: string;
  badge: string;
  category: string;
  url: string | null;
  kind: 'product' | 'action' | 'application';
  /** Set only where the thing has an icon of its own; a product is drawn by its monogram. */
  icon?: string;
};

/** GET /api/products/directory — the whole catalog plus this user's stored lists. */
export type ProductDirectoryResponse = {
  current: string;
  products: ProductDirectoryEntry[];
  categories: ProductDirectoryCategory[];
  favorites: string[];
  jobs: ProductDirectoryJob[];
  applications: ProductDirectoryApplication[];
  shortcuts: ProductDirectoryShortcut[];
};

type ProductDirectoryApiOptions = {
  logoutUrl: string;
};

/**
 * The fetch layer behind the product switcher, carrying the same 401 broadcast and MFA
 * redirect as the rest of the shell. The endpoints are libs-owned and mounted for every
 * app by SessionLoader, so there is no contract and no per-app client.
 */
export function createProductDirectoryApi(options: ProductDirectoryApiOptions) {
  async function send<T>(url: string, init?: RequestInit): Promise<T> {
    const response = await fetch(url, { credentials: 'same-origin', ...init });
    if (!response.ok) {
      if (response.status === 401) {
        notifyUnauthenticated(options.logoutUrl);
      } else {
        await notifyMfaEnrollmentRequiredFromResponse(response);
      }
      throw new Error(`Request failed: ${response.status}`);
    }
    return response.json() as Promise<T>;
  }

  async function getDirectory(): Promise<ProductDirectoryResponse> {
    return send<ProductDirectoryResponse>('/api/products/directory');
  }

  async function saveFavorites(products: string[]): Promise<string[]> {
    const body = await send<{ products: string[] }>('/api/products/favorites', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ products: products }),
    });
    return body.products;
  }

  return { getDirectory: getDirectory, saveFavorites: saveFavorites };
}
